/**
 * Hộp thoại tiếng Việt thay cho window.confirm / prompt / alert (nút native của
 * trình duyệt là "Cancel/OK" theo ngôn ngữ máy — không dịch được). Dùng API dạng
 * Promise, gọi ở bất cứ đâu:
 *
 *   if (await confirmVN('Xóa?', { danger: true, okText: 'Xóa' })) { ... }
 *   const name = await promptVN('Tên mới:', cũ);   // null nếu bấm Hủy
 *   await alertVN('Đã xảy ra lỗi …');
 *
 * <DialogHost/> phải được mount 1 lần ở gốc app (App.jsx).
 */
import { useEffect, useRef, useState } from 'react';

let _open = null; // hàm mở dialog do DialogHost đăng ký

export function confirmVN(message, { title, okText = 'Đồng ý', cancelText = 'Hủy', danger = false } = {}) {
  return new Promise((resolve) => {
    if (!_open) { resolve(window.confirm(message)); return; } // fallback nếu host chưa mount
    _open({ type: 'confirm', message, title, okText, cancelText, danger, resolve });
  });
}

export function promptVN(message, defaultValue = '', { title, okText = 'OK', cancelText = 'Hủy', password = false } = {}) {
  return new Promise((resolve) => {
    if (!_open) { resolve(window.prompt(message, defaultValue)); return; }
    _open({ type: 'prompt', message, title, defaultValue, okText, cancelText, password, resolve });
  });
}

export function alertVN(message, { title, okText = 'Đóng' } = {}) {
  return new Promise((resolve) => {
    if (!_open) { window.alert(message); resolve(); return; }
    _open({ type: 'alert', message, title, okText, resolve });
  });
}

export function DialogHost() {
  const [dlg, setDlg] = useState(null);
  const [val, setVal] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    _open = (d) => { setVal(d.defaultValue || ''); setDlg(d); };
    return () => { _open = null; };
  }, []);

  useEffect(() => {
    if (dlg && dlg.type === 'prompt' && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [dlg]);

  useEffect(() => {
    if (!dlg) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); finish(dlg.type === 'prompt' ? null : false); }
      else if (e.key === 'Enter' && dlg.type !== 'prompt') { e.preventDefault(); finish(dlg.type === 'alert' ? undefined : true); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line
  }, [dlg, val]);

  if (!dlg) return null;

  const finish = (result) => { const done = dlg.resolve; setDlg(null); done(result); };
  const onOk = () => finish(dlg.type === 'prompt' ? val : (dlg.type === 'alert' ? undefined : true));
  const onCancel = () => finish(dlg.type === 'prompt' ? null : false);

  return (
    <div className="vn-dialog-overlay" onClick={(e) => { if (e.target === e.currentTarget && dlg.type !== 'alert') onCancel(); }}>
      <style>{DIALOG_CSS}</style>
      <div className="vn-dialog" role="dialog" aria-modal="true">
        {dlg.title && <div className="vn-dialog-title">{dlg.title}</div>}
        <div className="vn-dialog-msg">{dlg.message}</div>
        {dlg.type === 'prompt' && (
          <input
            ref={inputRef}
            className="vn-dialog-input"
            type={dlg.password ? 'password' : 'text'}
            value={val}
            onChange={(e) => setVal(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onOk(); } if (e.key === 'Escape') { e.preventDefault(); onCancel(); } }}
          />
        )}
        <div className="vn-dialog-btns">
          {dlg.type !== 'alert' && <button type="button" className="vn-btn" onClick={onCancel}>{dlg.cancelText || 'Hủy'}</button>}
          <button type="button" className={`vn-btn ${dlg.danger ? 'vn-btn-danger' : 'vn-btn-primary'}`} onClick={onOk}>{dlg.okText || 'Đồng ý'}</button>
        </div>
      </div>
    </div>
  );
}

const DIALOG_CSS = `
@keyframes vnDlgFade{from{opacity:0}to{opacity:1}}
@keyframes vnDlgPop{from{opacity:0;transform:scale(.96)}to{opacity:1;transform:scale(1)}}
.vn-dialog-overlay{position:fixed;inset:0;z-index:400;background:rgba(18,21,43,.5);display:flex;align-items:center;justify-content:center;padding:20px;animation:vnDlgFade .16s ease}
.vn-dialog{background:var(--bg2,#fff);border:1px solid var(--border,#e5e7eb);border-radius:16px;padding:22px 22px 18px;width:420px;max-width:100%;box-shadow:0 24px 60px -12px rgba(18,21,43,.5);animation:vnDlgPop .2s cubic-bezier(.2,.8,.2,1)}
.vn-dialog-title{font-weight:700;font-size:17px;font-family:var(--head,inherit);margin-bottom:8px;color:var(--text,#111)}
.vn-dialog-msg{font-size:14.5px;line-height:1.6;color:var(--text2,#374151);white-space:pre-line;word-break:break-word}
.vn-dialog-input{width:100%;margin-top:14px;background:var(--bg3,#f3f4f6);border:1px solid var(--border,#e5e7eb);border-radius:9px;padding:10px 12px;color:var(--text,#111);font-size:15px;font-family:inherit;outline:none}
.vn-dialog-input:focus{border-color:var(--blue,#4f6ef7);box-shadow:0 0 0 3px var(--blue-bg,rgba(79,110,247,.15))}
.vn-dialog-btns{display:flex;gap:10px;justify-content:flex-end;margin-top:20px}
.vn-btn{padding:9px 18px;font-size:14px;font-weight:600;font-family:inherit;border:1px solid var(--border,#e5e7eb);border-radius:10px;cursor:pointer;background:var(--bg3,#f3f4f6);color:var(--text,#111);transition:all .15s}
.vn-btn:hover{background:var(--border,#e5e7eb)}
.vn-btn-primary{background:var(--blue,#4f6ef7);color:#fff;border-color:var(--blue,#4f6ef7)}
.vn-btn-primary:hover{filter:brightness(.95);background:var(--blue,#4f6ef7)}
.vn-btn-danger{background:var(--red,#ef4444);color:#fff;border-color:var(--red,#ef4444)}
.vn-btn-danger:hover{filter:brightness(.95);background:var(--red,#ef4444)}
`;
